import React, { useEffect, useState } from "react";
import { webOrigin } from "@/lib/nativeBridge";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import PublicShell from "@/components/PublicShell";
import SeoHead from "@/components/SeoHead";
import EliteBadge from "@/components/EliteBadge";
import VerifiedStandardsBadge from "@/components/VerifiedStandardsBadge";
import BreederReviews from "@/components/BreederReviews";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { setPostAuthRedirect } from "@/lib/post-auth-redirect";
import { UsersFourIcon as UsersFour, DogIcon as Dog, GenderMaleIcon as GenderMale, GenderFemaleIcon as GenderFemale, ShareIcon as Share, CheckCircleIcon as CheckCircle, EnvelopeSimpleIcon as EnvelopeSimple } from "@phosphor-icons/react";
import { thumbUrl, photoUrl, formatDate, formatApiError } from "@/lib/utils-app";
import { toast } from "sonner";
import MicrochipProtectedBadge from "@/components/MicrochipProtectedBadge";
import DNAVerifiedBadge from "@/components/DNAVerifiedBadge";
import ReportButton from "@/components/ReportButton";
import WatchButton from "@/components/WatchButton";
import VerifiedInquiryDialog from "@/components/VerifiedInquiryDialog";
import { WaxSeal } from "@/components/luxury";

export default function PublicBreeder() {
  const { id } = useParams();
  const [breeder, setBreeder] = useState(null);
  const [dogs, setDogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);
  const [inquireOpen, setInquireOpen] = useState(false);
  const [claimOpen, setClaimOpen] = useState(false);
  const [claimMsg, setClaimMsg] = useState("");
  const [claimBusy, setClaimBusy] = useState(false);
  const [claimSubmitted, setClaimSubmitted] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      api.get(`/public/breeders/${id}`),
      api.get(`/public/breeders/${id}/dogs`),
    ])
      .then(([b, d]) => {
        setBreeder(b.data);
        setDogs(d.data);
        document.title = `${b.data.kennel_name || b.data.name} · Armstrong K9 Registry`;
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    return () => {
      document.title = "Armstrong K9 Registry";
    };
  }, [id]);

  const share = async () => {
    // Use the OG-rich backend share URL so social platforms auto-render
    // the 1200×630 kennel preview card when this link is pasted.
    const slugOrId = breeder?.slug || id;
    const url = `${process.env.REACT_APP_BACKEND_URL || ""}/api/public/breeders/${slugOrId}/share`;
    try {
      if (navigator.share) {
        await navigator.share({
          title: breeder?.kennel_name || breeder?.name || "Breeder",
          url,
        });
        return;
      }
    } catch {
      /* fall through to copy */
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied — link preview will auto-render on social.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy link");
    }
  };

  if (loading) {
    return (
      <PublicShell>
        <div className="text-[#6B6B6B]">Loading…</div>
      </PublicShell>
    );
  }
  if (notFound || !breeder) {
    return (
      <PublicShell>
        <div className="bg-white border border-[#EAEAEA] rounded-xl p-12 text-center">
          <UsersFour size={40} weight="duotone" className="text-[#6B6B6B]/40 mx-auto mb-3" />
          <p className="text-[#6B6B6B]">Breeder profile not found.</p>
        </div>
      </PublicShell>
    );
  }

  const startClaim = () => {
    if (!user) {
      setPostAuthRedirect(`/k/${breeder.slug || id}`);
      toast.info("Create a free account first — you'll be brought right back here to claim your kennel.");
      navigate("/register");
      return;
    }
    setClaimOpen(true);
  };

  const submitClaim = async () => {
    setClaimBusy(true);
    try {
      const { data } = await api.post(`/breeders/${breeder.id}/claim`, { message: claimMsg });
      if (data.status === "claimed") {
        toast.success("Kennel claimed! It's now linked to your account.");
        navigate(`/breeders/${breeder.id}`);
      } else {
        toast.success(
          data.already_submitted
            ? "You already have a claim pending — we'll email you once it's reviewed."
            : "Claim submitted — our team will verify and email you."
        );
        setClaimOpen(false);
        setClaimSubmitted(true);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setClaimBusy(false);
    }
  };

  // ===== Unclaimed listing — limited public view + claim flow =====
  if (breeder.unclaimed) {
    return (
      <PublicShell>
        <div
          className="bg-[#0E0E0E] text-white rounded-xl p-8 md:p-12 relative overflow-hidden"
          data-testid="public-breeder-unclaimed"
        >
          <div aria-hidden className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-white/5 blur-3xl" />
          <div className="relative max-w-2xl">
            <span
              className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.25em] bg-white/10 border border-white/20 text-white/80 rounded-full px-3 py-1"
              data-testid="unclaimed-badge"
            >
              Unclaimed listing
            </span>
            <h1
              className="font-spectral font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mt-5"
              data-testid="public-breeder-name"
            >
              {breeder.kennel_name || breeder.name}
            </h1>
            {breeder.location && <p className="text-white/60 mt-3">{breeder.location}</p>}
            <div className="gold-hairline mt-6 mb-6 max-w-md opacity-50" />
            <p className="text-white/75 leading-relaxed" data-testid="unclaimed-explainer">
              This kennel hasn't joined Armstrong K9 Registry yet. This listing
              was added by a community member for pedigree record-keeping —
              contact details, reviews, and the dog roster stay hidden until
              the kennel's owner claims the profile.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              {claimSubmitted ? (
                <span
                  className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-5 h-11 text-sm font-semibold"
                  data-testid="claim-submitted-note"
                >
                  <CheckCircle size={16} weight="fill" className="text-gold-light" />
                  Claim submitted — we'll email you once it's verified
                </span>
              ) : (
                <Button
                  onClick={startClaim}
                  className="rounded-full bg-gold-gradient hover:opacity-95 text-ink font-semibold px-6 h-11 border-0 shadow-[0_8px_24px_rgba(201,162,39,0.35)]"
                  data-testid="claim-kennel-btn"
                >
                  This is my kennel — claim it
                </Button>
              )}
              <Link
                to="/kennels"
                className="text-sm text-white/60 hover:text-white underline"
                data-testid="back-to-kennels-link"
              >
                Browse claimed kennels →
              </Link>
            </div>
          </div>
        </div>

        <Dialog open={claimOpen} onOpenChange={setClaimOpen}>
          <DialogContent data-testid="claim-kennel-dialog">
            <DialogHeader>
              <DialogTitle>Claim {breeder.kennel_name || breeder.name}</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-[#6B6B6B]">
              Tell us briefly how we can verify this kennel is yours (website,
              social media, phone). If your account email matches the kennel's
              contact email the claim is instant — otherwise our team reviews
              it and emails you.
            </p>
            <Textarea
              value={claimMsg}
              onChange={(e) => setClaimMsg(e.target.value)}
              placeholder="I'm the owner of this kennel — you can verify me via…"
              rows={4}
              data-testid="claim-message-input"
            />
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setClaimOpen(false)}
                className="rounded-full"
                data-testid="claim-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={submitClaim}
                disabled={claimBusy}
                className="rounded-full bg-ink hover:bg-crimson text-white"
                data-testid="claim-submit-btn"
              >
                {claimBusy ? "Submitting…" : "Submit claim"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PublicShell>
    );
  }

  const canonicalUrl = `${webOrigin()}/k/${breeder.slug || breeder.id}`;
  // Prefer the kennel's own photo for OG / social preview if the
  // owner has uploaded one. Falls back to the first dog photo, then
  // to the platform logo.
  const kennelPhoto = breeder.photo_url || breeder.logo_url;
  const ogImage = kennelPhoto
    ? (kennelPhoto.startsWith("http") ? kennelPhoto : `${webOrigin()}${kennelPhoto}`)
    : (dogs[0]?.photos?.[0]
        ? `${webOrigin()}/api/files/${dogs[0].photos[0]}`
        : `${webOrigin()}/logo-512.png`);

  return (
    <PublicShell>
      <SeoHead
        title={`${breeder.kennel_name || breeder.name} · Armstrong K9 Registry`}
        description={(breeder.bio || `Browse dogs and litters from ${breeder.kennel_name || breeder.name}, a verified breeder on the Armstrong K9 Registry.`).slice(0, 200)}
        imageUrl={ogImage}
        url={canonicalUrl}
        type="profile"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "@id": canonicalUrl,
          "name": breeder.kennel_name || breeder.name,
          "description": breeder.bio || "",
          "image": ogImage,
          "address": breeder.location
            ? { "@type": "PostalAddress", "addressLocality": breeder.location }
            : undefined,
          "url": canonicalUrl,
          "makesOffer": dogs.filter((d) => d.for_sale && d.asking_price).slice(0, 12).map((d) => ({
            "@type": "Offer",
            "itemOffered": { "@type": "Product", "name": d.name, "category": d.breed },
            "price": d.asking_price,
            "priceCurrency": d.sale_currency || "USD",
            "url": `${webOrigin()}/d/${d.slug || d.id}`,
          })),
        }}
        breadcrumbs={[
          { name: "Home", url: `${webOrigin()}/` },
          { name: "Kennels", url: `${webOrigin()}/kennels` },
          { name: breeder.kennel_name || breeder.name, url: canonicalUrl },
        ]}
      />
      {/* Hero */}
      <div
        className="bg-[#0E0E0E] text-white border border-[#0E0E0E] rounded-xl p-8 md:p-10 mb-8 relative overflow-hidden"
        data-testid="public-breeder-hero"
      >
        {breeder.hero_video_url ? (
          <video
            src={breeder.hero_video_url}
            poster={breeder.hero_video_poster_url || undefined}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            className="absolute inset-0 w-full h-full object-cover opacity-50"
            aria-hidden
            data-testid="public-breeder-hero-video"
          />
        ) : null}
        {breeder.hero_video_url ? (
          <div
            aria-hidden
            className="absolute inset-0 bg-gradient-to-r from-[#0E0E0E]/85 via-[#0E0E0E]/55 to-[#0E0E0E]/85"
          />
        ) : (
          <div
            aria-hidden
            className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-[#C1272D]/30 blur-3xl"
          />
        )}
        <div className="relative flex flex-col md:flex-row md:items-start md:justify-between gap-6">
          <div className="flex items-start gap-4 sm:gap-5 flex-1 min-w-0">
            <div
              className="h-20 w-20 sm:h-24 sm:w-24 rounded-full overflow-hidden bg-white/10 ring-1 ring-gold/40 flex items-center justify-center flex-shrink-0"
              data-testid="public-breeder-photo"
            >
              {kennelPhoto ? (
                <img
                  src={thumbUrl(kennelPhoto)}
                  alt={breeder.kennel_name || breeder.name || "Kennel"}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-3xl sm:text-4xl font-spectral text-gold-light select-none">
                  {(breeder.kennel_name || breeder.name || "?").trim().charAt(0).toUpperCase()}
                </span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold-light mb-3">
                Kennel profile
              </p>
              <h1
                className="font-spectral font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] flex items-center gap-3 flex-wrap"
                data-testid="public-breeder-name"
              >
                {breeder.kennel_name || breeder.name}
                {(breeder.verified || breeder.is_verified) && <WaxSeal size={44} title="Verified kennel" />}
                <EliteBadge elite={breeder.elite_verified} size="md" />
              </h1>
              {breeder.kennel_name && (
                <p className="text-white/70 mt-2">{breeder.name}</p>
              )}
              <div className="gold-hairline mt-4 mb-4 max-w-md opacity-60" />
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-white/70 font-tabular">
                {breeder.location && <span className="font-sans">{breeder.location}</span>}
                {breeder.contact && <span>{breeder.contact}</span>}
              </div>
              {breeder.bio && (
                <p className="mt-5 text-white/85 leading-relaxed max-w-2xl whitespace-pre-wrap">
                  {breeder.bio}
                </p>
              )}
            </div>
          </div>
          <div className="flex flex-row md:flex-col gap-2 md:self-start flex-shrink-0 flex-wrap">
            <Button
              onClick={share}
              className="rounded-full bg-gold-gradient hover:opacity-95 text-ink font-semibold border-0 shadow-[0_8px_24px_rgba(201,162,39,0.35)]"
              data-testid="public-breeder-share-btn"
            >
              {copied ? (
                <>
                  <CheckCircle size={16} className="mr-1" weight="fill" /> Copied
                </>
              ) : (
                <>
                  <Share size={16} className="mr-1" /> Share profile
                </>
              )}
            </Button>
            {/* Watch CTA — pairs an icon WatchButton with a labeled
                wrapper so visitors get an obvious "follow this kennel"
                call to action. WatchButton itself handles the locked
                (no Buyer Pass) → /billing fallback. */}
            <div
              className="inline-flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 text-white text-sm font-semibold px-4 h-10 transition-colors"
              data-testid="public-breeder-watch-wrap"
            >
              <WatchButton targetType="breeder" targetId={breeder.id} size={16} className="!h-6 !w-6 !rounded-full !bg-transparent !border-0 hover:!bg-transparent !shadow-none" />
              <span>Watch kennel</span>
            </div>
            <ReportButton
              targetType="breeder"
              targetId={breeder.slug || id}
              targetLabel={breeder.kennel_name || breeder.name}
              testIdPrefix="public-breeder-report"
              className="text-white/70 hover:text-white"
            />
          </div>
          {/* Verified Inquiry — primary CTA closing the marketplace
              leakage. Renders for ALL kennels so buyers always go
              through Armstrong's email relay; gated kennels also
              charge the $9 buyer intro fee in the dialog. */}
          <Button
            onClick={() => setInquireOpen(true)}
            className="mt-4 rounded-full bg-white text-ink hover:bg-[#FBF7EC] font-semibold px-5 h-11"
            data-testid="public-breeder-contact-cta"
          >
            <EnvelopeSimple size={16} weight="duotone" className="mr-1.5" />
            Contact this kennel
          </Button>
        </div>
      </div>

      <VerifiedInquiryDialog
        open={inquireOpen}
        onOpenChange={setInquireOpen}
        breeder={breeder}
      />

      {/* Buyer reviews */}
      <BreederReviews breederUserId={breeder.user_id} currentUserId={user?.id} />

      {/* Pinned testimonials — Elite-only kennel feature */}
      {(breeder.pinned_testimonials || []).length > 0 && (
        <section
          className="bg-white border border-[#EAEAEA] rounded-xl p-6 md:p-8 mb-8"
          data-testid="public-breeder-pinned-testimonials"
        >
          <div className="flex items-baseline justify-between gap-3 mb-5 flex-wrap">
            <h2 className="font-spectral text-2xl tracking-tight">Pinned testimonials</h2>
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-gold">
              Hand-picked by the kennel
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {breeder.pinned_testimonials.map((t, i) => (
              <figure
                key={i}
                className="border-l-2 border-gold pl-5 py-1"
                data-testid={`pinned-testimonial-${i}`}
              >
                <blockquote className="font-spectral text-lg leading-snug text-ink italic">
                  &ldquo;{t.quote}&rdquo;
                </blockquote>
                <figcaption className="mt-3 text-sm text-[#6B6B6B]">
                  — <span className="font-semibold text-ink">{t.author}</span>
                  {t.year ? <span className="ml-2 text-xs">· {t.year}</span> : null}
                  {t.source_url ? (
                    <>
                      {" "}
                      ·{" "}
                      <a
                        href={t.source_url}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="text-crimson hover:underline text-xs font-semibold uppercase tracking-wider"
                      >
                        Source ↗
                      </a>
                    </>
                  ) : null}
                </figcaption>
              </figure>
            ))}
          </div>
        </section>
      )}

      {/* Award case — Elite-only kennel feature */}
      {(breeder.award_case || []).length > 0 && (
        <section
          className="bg-[#0E0E0E] text-white rounded-xl p-6 md:p-8 mb-8 relative overflow-hidden"
          data-testid="public-breeder-award-case"
        >
          <div
            aria-hidden
            className="absolute -right-16 -bottom-16 w-48 h-48 rounded-full bg-gold/15 blur-3xl"
          />
          <div className="relative">
            <div className="flex items-baseline justify-between gap-3 mb-5 flex-wrap">
              <h2 className="font-spectral text-2xl tracking-tight">Award case</h2>
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-gold-light">
                Earned, not bought
              </span>
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
              {breeder.award_case.map((a, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 text-sm border-b border-white/10 pb-3"
                  data-testid={`award-case-${i}`}
                >
                  <span className="text-gold mt-0.5 font-bold">★</span>
                  <div className="min-w-0 flex-1">
                    <div className="font-spectral text-base leading-snug">{a.title}</div>
                    <div className="text-xs text-white/60 mt-0.5">
                      {a.year ? <span>{a.year}</span> : null}
                      {a.year && a.issuer ? " · " : null}
                      {a.issuer ? <span>{a.issuer}</span> : null}
                      {a.url ? (
                        <>
                          {a.year || a.issuer ? " · " : null}
                          <a
                            href={a.url}
                            target="_blank"
                            rel="noopener noreferrer nofollow"
                            className="text-gold-light hover:underline font-semibold uppercase tracking-wider"
                          >
                            Verify ↗
                          </a>
                        </>
                      ) : null}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Dogs grid */}
      <div className="flex items-baseline justify-between mb-5 flex-wrap gap-3">
        <h2 className="font-spectral text-2xl tracking-tight">
          Dogs from this kennel
          <span className="text-[#6B6B6B] text-base font-manrope ml-2">
            ({dogs.length})
          </span>
        </h2>
        {dogs.length > 0 && (() => {
          const protectedCount = dogs.filter((d) => d.microchip_protected).length;
          if (protectedCount === 0) return null;
          return (
            <div
              className="text-[11px] text-[#2F6E2F] flex items-center gap-2"
              data-testid="kennel-microchip-stat"
            >
              <MicrochipProtectedBadge size="sm" />
              <span>
                {protectedCount} of {dogs.length} dogs registered with a microchip
              </span>
            </div>
          );
        })()}
      </div>

      {dogs.length === 0 ? (
        <div className="bg-white border border-dashed border-[#E5E5E5] rounded-xl p-12 text-center">
          <Dog size={40} weight="duotone" className="text-[#6B6B6B]/40 mx-auto mb-3" />
          <p className="text-[#6B6B6B]">No dogs linked to this kennel yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {dogs.map((d) => (
            <PublicDogCard key={d.id} dog={d} />
          ))}
        </div>
      )}

      <div className="mt-12 border-t border-[#E5E5E5] pt-8 text-center">
        <p className="text-sm text-[#6B6B6B]">
          Want your own kennel page?{" "}
          <Link
            to="/register"
            className="text-ink font-semibold hover:text-crimson"
            data-testid="public-breeder-signup-cta"
          >
            Create a free account →
          </Link>
        </p>
      </div>
    </PublicShell>
  );
}

function PublicDogCard({ dog }) {
  const hasPhoto = Boolean(dog.photos?.[0]);
  return (
    <div
      className="bg-white border border-[#EAEAEA] rounded-xl overflow-hidden shadow-luxury-light hover:-translate-y-[2px] transition-transform"
      data-testid={`public-dog-card-${dog.id}`}
    >
      <div className="aspect-[4/3] bg-[#FAFAFA]">
        {hasPhoto ? (
          <img
            src={thumbUrl(dog.photos[0]) || photoUrl(dog.photos[0])}
            alt={dog.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Dog size={40} weight="duotone" className="text-[#6B6B6B]/40" />
          </div>
        )}
      </div>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-1">
          {dog.sex === "male" ? (
            <GenderMale size={16} className="text-ink" />
          ) : (
            <GenderFemale size={16} className="text-crimson" />
          )}
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
            {dog.breed}
          </span>
        </div>
        <div className="font-spectral text-lg tracking-tight truncate">{dog.name}</div>
        <div className="mt-1.5">
          <MicrochipProtectedBadge protected={!!dog.microchip_protected} size="sm" />
          {dog.dna_verified && (
            <div className="mt-1">
              <DNAVerifiedBadge verified size="sm" />
            </div>
          )}
        </div>
        <div className="text-xs text-[#6B6B6B] mt-1 space-y-0.5">
          {dog.dob && <div>Born {formatDate(dog.dob)}</div>}
          {dog.registration_number && (
            <div className="font-mono truncate">Reg #{dog.registration_number}</div>
          )}
        </div>
        {dog.titles?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1">
            {dog.titles.slice(0, 3).map((t) => (
              <span
                key={t.id}
                className="bg-[#C1272D]/10 text-crimson border border-[#C1272D]/20 rounded-full px-2 py-0.5 text-[10px] font-semibold"
              >
                {t.title}
              </span>
            ))}
            {dog.titles.length > 3 && (
              <span className="text-[10px] text-[#6B6B6B] self-center">
                +{dog.titles.length - 3}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
